<template>
  <div
    :class="rootClasses"
    role="separator"
    :aria-orientation="ariaOrientation"
    :aria-label="shouldRenderLabel ? props.label : undefined"
    data-cy="ds-divider"
  >
    <span
      :class="DS_DIVIDER_CLASSES.Line"
      aria-hidden="true"
      data-cy="ds-divider__line"
    />
    <span
      v-if="shouldRenderLabel"
      :class="DS_DIVIDER_CLASSES.Label"
      data-cy="ds-divider__label"
    >
      {{ props.label }}
    </span>
    <span
      v-if="shouldRenderLabel"
      :class="DS_DIVIDER_CLASSES.Line"
      aria-hidden="true"
      data-cy="ds-divider__line"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { DSDividerProps } from "@/src/design_system/layouts_and_containers/divider/types";
import { DS_DIVIDER_SEVERITY } from "@/src/design_system/layouts_and_containers/divider/types";
import { DS_DIRECTION, type DSDirection } from "@/src/design_system/shared/types/direction";
import {
  DS_DIVIDER_CLASSES,
  DS_DIVIDER_DIRECTION_TO_CLASS_MAP,
  DS_DIVIDER_SEVERITY_TO_CLASS_MAP,
} from "@/src/design_system/layouts_and_containers/divider/constants/classes";

const DIRECTION_TO_ARIA_ORIENTATION = {
  [DS_DIRECTION.Horizontally]: "horizontal",
  [DS_DIRECTION.Vertically]: "vertical",
} as const satisfies Record<DSDirection, "horizontal" | "vertical">;

const props = withDefaults(defineProps<DSDividerProps>(), {
  orientation: DS_DIRECTION.Horizontally,
  severity: DS_DIVIDER_SEVERITY.Default,
  label: undefined,
});

const normalizedDirection = computed(() => {
  return Object.hasOwn(DS_DIVIDER_DIRECTION_TO_CLASS_MAP, props.orientation)
    ? props.orientation
    : DS_DIRECTION.Horizontally;
});

const shouldRenderLabel = computed(() => {
  return normalizedDirection.value === DS_DIRECTION.Horizontally && Boolean(props.label);
});

const ariaOrientation = computed(() => DIRECTION_TO_ARIA_ORIENTATION[normalizedDirection.value]);

const orientationClass = computed(() => DS_DIVIDER_DIRECTION_TO_CLASS_MAP[normalizedDirection.value]);

const severityClass = computed(() => {
  return (
    DS_DIVIDER_SEVERITY_TO_CLASS_MAP[props.severity] ?? DS_DIVIDER_SEVERITY_TO_CLASS_MAP[DS_DIVIDER_SEVERITY.Default]
  );
});

const rootClasses = computed(() => [DS_DIVIDER_CLASSES.Root, orientationClass.value, severityClass.value]);
</script>
